/**
 * useUpdateAvailable — checks the release manifest and decides whether to prompt (Card 10).
 *
 * NATIVE ONLY. On web the service worker already auto-updates (`registerType: 'autoUpdate'` in
 * vite.config.ts, plus the `controllerchange` reload in main.tsx), so there is nothing to prompt
 * for; the hook short-circuits and reports no update. On native there is no service-worker update
 * event for the *binary*, so we fetch the manifest ourselves.
 *
 * Event-driven, NOT polled: once on mount, and again whenever the app returns to the foreground.
 * There is deliberately no interval — a timer would only ever fire if the app stayed continuously
 * foregrounded, and the WebView throttles/suspends JS timers in the background anyway, so it would
 * be dead weight. Resuming the app is the moment a user could act on an update, and that is
 * exactly when `appStateChange` fires.
 *
 * Contract: src/docs/MOBILE_VERSION_SCHEME.md §5
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { fetchReleaseManifest, postReleaseEvent } from '@/api/extensions/release-api';
import { resolveUpdateChannel } from '@/config/update-channel';
import { isNative, platform } from '@/utils/platform';
import {
  artifactFor,
  isNewerVersion,
  isUpdateMandatory,
  type ReleaseArtifact,
  type ReleaseManifest,
  type ReleasePlatform
} from '@/types/release';

// The channel is a property of the INSTALL, not of this bundle: an OTA bundle is promoted from
// beta to production byte for byte, so anything compiled in here would be wrong for every promoted
// build. It is read from the binary at runtime and cached — see src/config/update-channel.ts,
// which is where the reasoning and the safety argument live. This used to be a hardcoded 'beta'.

const DISMISSED_KEY = 'tradeloop.updateBanner.dismissedVersion';
const ADOPTION_KEY = 'tradeloop.release.adoptionReported';

/** localStorage throws in private-mode webviews; a telemetry guard must never break the app. */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export interface UpdateAvailableState {
  /** True when a strictly newer version is published AND the user has not dismissed it. */
  updateAvailable: boolean;
  /** True when the running version is below the manifest's `minNativeVersion` floor. */
  mandatory: boolean;
  manifest: ReleaseManifest | null;
  /** The build to download. Undefined when this release ships no artifact for this platform. */
  artifact: ReleaseArtifact | undefined;
  /** The running native version, from `App.getInfo()`. Null on web / before it resolves. */
  runningVersion: string | null;
  /** Records a `download` event, then resolves. Callers navigate afterwards. */
  recordDownload: () => Promise<void>;
  /** Hides the banner for this version. A no-op while the update is mandatory. */
  dismiss: () => void;
}

export function useUpdateAvailable(): UpdateAvailableState {
  const [manifest, setManifest] = useState<ReleaseManifest | null>(null);
  const [runningVersion, setRunningVersion] = useState<string | null>(null);
  const [runningBuild, setRunningBuild] = useState<number | undefined>(undefined);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() => safeGet(DISMISSED_KEY));

  // Guards the once-per-version adoption beacon against React 18 StrictMode double-effects.
  const adoptionSent = useRef(false);

  // Read the running NATIVE version. Not the web bundle's version: the manifest describes the
  // native binary, so the comparison must be against what Android/iOS actually installed.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;

    App.getInfo()
      .then((info) => {
        if (cancelled) return;
        setRunningVersion(info.version);
        const build = Number(info.build);
        setRunningBuild(Number.isFinite(build) ? build : undefined);
      })
      .catch(() => {
        /* No app info => no reliable comparison => stay silent. */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!isNative) return;
    const channel = await resolveUpdateChannel();
    const next = await fetchReleaseManifest(platform as ReleasePlatform, channel);
    // `null` means "no information" (offline / 404 / 503). Keep whatever we already had rather
    // than flapping the banner off on a transient failure.
    if (next) setManifest(next);
  }, []);

  // Check once on mount, then only when the app returns to the foreground. No interval.
  useEffect(() => {
    if (!isNative) return;

    void refresh();

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void refresh();
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [refresh]);

  // Report adoption once per version, the first time this build runs.
  useEffect(() => {
    if (!isNative || !runningVersion || adoptionSent.current) return;
    if (safeGet(ADOPTION_KEY) === runningVersion) return;

    adoptionSent.current = true;
    void resolveUpdateChannel()
      .then((channel) =>
        postReleaseEvent({
          event: 'adoption',
          platform: platform as ReleasePlatform,
          channel,
          version: runningVersion,
          buildNumber: runningBuild
        })
      )
      .then((ok) => {
        // Only mark it reported when the write actually landed, so a transient outage retries
        // on the next launch instead of losing the event for this version forever.
        if (ok) safeSet(ADOPTION_KEY, runningVersion);
      });
  }, [runningVersion, runningBuild]);

  const hasNewer = Boolean(manifest && runningVersion && isNewerVersion(manifest.version, runningVersion));
  const mandatory = Boolean(manifest && runningVersion && isUpdateMandatory(manifest, runningVersion));
  const artifact = manifest ? artifactFor(manifest, platform as ReleasePlatform) : undefined;

  // A mandatory update overrides a previous dismissal — that is the one case where nagging is
  // correct. Without an artifact there is nothing to offer, so stay quiet.
  const dismissedThisVersion = Boolean(manifest && dismissedVersion === manifest.version);
  const updateAvailable = hasNewer && Boolean(artifact) && (mandatory || !dismissedThisVersion);

  const recordDownload = useCallback(async () => {
    if (!manifest || !runningVersion) return;
    await postReleaseEvent({
      event: 'download',
      platform: platform as ReleasePlatform,
      // Already resolved by the time a banner exists to click, so this is a cached read.
      channel: await resolveUpdateChannel(),
      version: manifest.version,
      buildNumber: manifest.buildNumber
    });
  }, [manifest, runningVersion]);

  const dismiss = useCallback(() => {
    if (!manifest || mandatory) return;
    safeSet(DISMISSED_KEY, manifest.version);
    setDismissedVersion(manifest.version);
  }, [manifest, mandatory]);

  return {
    updateAvailable,
    mandatory,
    manifest,
    artifact,
    runningVersion,
    recordDownload,
    dismiss
  };
}
